/**
 * Shared product preference cookie utility.
 *
 * The `selectedProduct` cookie is the single source of truth for which
 * product (Exams or Open) a user has chosen. Both Next.js middlewares
 * read it to route users to the correct app, and the native Capacitor
 * shell syncs it on boot so the web layer sees the preference immediately.
 */

import {
  removeNativePreference,
  setNativePreference,
} from "./native-preferences";

export type Product = "exams" | "open";

export const PRODUCT_COOKIE = "selectedProduct";

const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;

/** Returns `.medlyai.com` in production, undefined on localhost (so the cookie stays on the current host). */
function getCookieDomain(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const host = window.location.hostname;
  if (
    host === "localhost" ||
    host === "127.0.0.1" ||
    /^(10|192\.168|172\.(1[6-9]|2\d|3[01]))\./.test(host)
  ) {
    return undefined;
  }
  return ".medlyai.com";
}

/** Sets the product cookie and optionally syncs to Capacitor Preferences. */
export async function setProductCookie(product: Product): Promise<void> {
  const domain = getCookieDomain();
  const domainPart = domain ? `; domain=${domain}` : "";
  document.cookie = `${PRODUCT_COOKIE}=${product}; path=/; max-age=${ONE_YEAR_SECONDS}; SameSite=Lax${domainPart}`;

  // Sync to Capacitor Preferences so the native boot redirect works
  if (isCapacitorNative()) {
    await setNativePreference("selectedPlatform", product);
  }
}

/** Removes the product cookie and Capacitor Preferences entry. */
export async function clearProductCookie(): Promise<void> {
  const domain = getCookieDomain();
  const domainPart = domain ? `; domain=${domain}` : "";
  document.cookie = `${PRODUCT_COOKIE}=; path=/; max-age=0${domainPart}`;

  if (isCapacitorNative()) {
    await removeNativePreference("selectedPlatform");
  }
}

export function isCapacitorNative(): boolean {
  if (typeof window === "undefined") return false;
  const cap = (window as unknown as Record<string, unknown>).Capacitor;
  if (!cap || typeof cap !== "object") return false;
  const isNative = (cap as Record<string, unknown>).isNativePlatform;
  return typeof isNative === "function" && (isNative as () => boolean)();
}
